import type {
  EsimBalance,
  EsimPlan,
  EsimProduct,
  EsimProfile,
} from "@/lib/types/esim";

/** Megabytes in a gigabyte, matching the conversion the API's cap uses. */
const MB_PER_GB = 1024;

/** Validity units DT One returns, mapped to their translation key suffix. */
const VALIDITY_UNIT_KEYS: Record<string, string> = {
  DAY: "day",
  WEEK: "week",
  MONTH: "month",
  YEAR: "year",
};

export function validityUnitKey(unit: string): string | null {
  return VALIDITY_UNIT_KEYS[unit?.toUpperCase()] ?? null;
}

/** DT One's allowance amount for an unlimited data plan (GD-155). */
const UNLIMITED_AMOUNT = -1;

/**
 * The data allowance of a plan, e.g. `10 GB`, or `unlimited` — the caller's
 * translated label — when DT One sends -1.
 *
 * DT One types an eSIM's data line as `unit_type: DATA`, but older catalogue
 * entries only set `type`, so both are checked before giving up — a plan card
 * without an allowance is worthless to a traveller.
 */
export function planDataAllowance(
  product: EsimProduct,
  unlimited: string,
): string | null {
  const data = product.benefits?.find(
    (benefit) => benefit.unit_type === "DATA" || benefit.type === "DATA",
  );

  const amount = data?.amount?.total_including_tax ?? data?.amount?.base;
  if (!data || amount === undefined || amount === null) return null;

  return formatAmount(amount, data.unit, unlimited);
}

/** The allowance DT One echoed back on a completed order, e.g. `10 GB`. */
export function orderDataAllowance(
  plan: EsimPlan | null,
  unlimited: string,
): string | null {
  if (!plan?.size) return null;
  return formatAmount(plan.size.amount, plan.size.unit, unlimited);
}

export function formatBalanceAmount(
  balance: EsimBalance,
  unlimited: string,
): string {
  return formatAmount(balance.amount, balance.unit, unlimited);
}

function formatAmount(amount: number, unit: string, unlimited: string): string {
  return amount === UNLIMITED_AMOUNT
    ? unlimited
    : `${trimNumber(amount)} ${unit}`;
}

function trimNumber(value: number): string {
  // `toFixed(2)` alone renders 7.5 as "7.50" and 1.1 as "1.10". Two decimals is
  // the right ceiling — a 500 MB plan has to read 0.49, not 0 — but the trailing
  // zeros are noise on an allowance, so the round-trip through Number drops
  // them while keeping 0.49 intact.
  return Number.isInteger(value)
    ? String(value)
    : String(Number(value.toFixed(2)));
}

/**
 * An allowance in MB as gigabytes, e.g. `7` or `1.5`.
 *
 * The API meters in MB and this is the only place that converts, so a 500 MB
 * plan reads `0.49` rather than being rounded away to `0` in one component and
 * `1` in another.
 *
 * Returns a number, not a string, because the caller hands it to `t()` as a
 * translation argument: ICU interpolates a bare `{amount}` verbatim, so a
 * pre-formatted string printed "0.49" in French, German, Spanish, Italian,
 * Portuguese and Turkish, all of which write "0,49". The messages ask for
 * `{amount, number}` and let `Intl.NumberFormat` pick the separator.
 *
 * Rounded here rather than in the message so the two decimals stay a property
 * of the figure — the exhausted check below compares against the number this
 * returns, and a message-side skeleton would let those two disagree.
 */
export function formatGb(megabytes: number): number {
  return Number((megabytes / MB_PER_GB).toFixed(2));
}

/**
 * The SM-DP+ server and matching id a customer types when their phone cannot
 * scan the QR. Both are already inside the LPA activation code —
 * `LPA:1$<smdp>$<matching-id>` — so they are parsed out rather than stored
 * twice.
 */
export function manualInstallDetails(profile: EsimProfile): {
  smdpAddress: string | null;
  activationCode: string;
} {
  const parts = profile.activationCode?.split("$") ?? [];

  return {
    smdpAddress: profile.smdpAddress ?? parts[1] ?? null,
    activationCode: parts[2] ?? profile.activationCode,
  };
}

/** ICCIDs are 19–20 digits — grouped in fours so they can be read aloud. */
export function formatIccid(iccid: string): string {
  return iccid.replace(/(.{4})/g, "$1 ").trim();
}

export function formatEsimDate(value: string | null, locale: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
}

/**
 * `FR` → 🇫🇷 (GD-98). DT One's `country_set` is ISO 3166 alpha-2 on a
 * single-country plan (checked on production rows, 2026-09-10); anything else
 * gets no flag.
 * ponytail: emoji flags render as two letters on Windows; swap in an SVG flag
 * set if desktop Windows traffic matters.
 */
export function countryFlag(isoCode: string | undefined): string | null {
  if (!isoCode || !/^[A-Z]{2}$/i.test(isoCode)) return null;

  const REGIONAL_INDICATOR_A = 0x1f1e6;
  return String.fromCodePoint(
    ...[...isoCode.toUpperCase()].map(
      (letter) => REGIONAL_INDICATOR_A + letter.charCodeAt(0) - 65,
    ),
  );
}
